/**
 * The Bookings page's standard look (signed-in-web.html): the title, then the Upcoming and Past tables. A host may put
 * pieces in the top place, between the title and Upcoming (web-studio-sites D12: Waiting on you and the next session on
 * a custom site's Bookings page, telify-pages.html).
 */
import type { ReactNode } from 'react';

import { partsOf, pieceRoot, usePlaced } from '../../contract/runtime';
import account from '../../styles/account.module.css';
import shared from '../../styles/shared.module.css';
import { Icon } from '../../ui/Icon';
import { onLink } from '../../ui/onLink';
import { Photo } from '../../ui/Photo';
import { contract, type BookingsListRow } from './contract';
import css from './BookingsList.module.css';
import type { BookingsListRuntime } from './useBookingsList';

const part = partsOf(contract);

const TONE = { live: '', done: account.statusDone, off: account.statusOff } as const;

function Row({ row, past, withPayment, open }: { row: BookingsListRow; past: boolean; withPayment: boolean; open: (href: string) => void }) {
  return (
    <a className={`${css.tr} ${row.tone === 'off' ? css.off : ''}`} role="row" href={row.href} onClick={onLink(() => open(row.href))} {...part('row', row.tone)}>
      <span className={css.date} role="cell">
        <span className={css.mon} {...part('month')}>
          {row.month}
        </span>
        <span className={css.day}>{row.day}</span>
        <span className={`${css.dow} ${row.today ? css.today : ''}`}>{row.weekday}</span>
      </span>
      <span className={css.sess} role="cell">
        {!past && <span className={css.thumb}>{row.photo && <Photo photo={row.photo} />}</span>}
        <span className={css.info}>
          {row.kicker && (
            <span className={css.kick}>
              <Icon name="users" small />
              {row.kicker}
            </span>
          )}
          <span className={css.name}>{row.name}</span>
          {row.withName && <span className={css.by}>{row.withName}</span>}
        </span>
      </span>
      <span className={css.when} role="cell">
        <span className={css.main}>{row.time}</span>
        {row.length && <span className={css.cellSub}>{row.length}</span>}
      </span>
      <span className={css.place} role="cell">
        <Icon name={row.place.byPhone ? 'phone' : row.place.online ? 'video' : 'pin'} small />
        <span>
          <span className={css.main}>{row.place.main}</span>
          {row.place.sub && <span className={css.cellSub}>{row.place.sub}</span>}
        </span>
      </span>
      <span className={css.stat} role="cell">
        <span className={`${account.status} ${css.st} ${TONE[row.tone]}`}>{row.status}</span>
      </span>
      {withPayment && (
        <span className={css.paid} role="cell">
          {row.payment?.amount && (
            <span className={css.amt} {...part('amount')}>
              {row.payment.amount}
            </span>
          )}
          {row.payment?.note && <span className={css.cellSub}>{row.payment.note}</span>}
        </span>
      )}
      <span className={css.rowLink} role="cell">
        View booking
        <Icon name="right" small />
      </span>
    </a>
  );
}

function Table({ label, rows, past, open }: { label: string; rows: readonly BookingsListRow[]; past: boolean; open: (href: string) => void }) {
  const withPayment = rows.some((r) => r.payment);
  return (
    <div className={`${css.tbl} ${past ? css.past : ''} ${withPayment ? css.withPayment : ''}`} role="table" aria-label={label} {...part(past ? 'past' : 'upcoming')}>
      <div className={`${css.tr} ${css.th}`} role="row">
        <span role="columnheader">Date</span>
        <span role="columnheader">Session</span>
        <span role="columnheader">Time</span>
        <span role="columnheader">Where</span>
        <span role="columnheader">Status</span>
        {withPayment && <span role="columnheader">Payment</span>}
        <span role="columnheader" />
      </div>
      {rows.map((r) => (
        <Row key={r.id} row={r} past={past} withPayment={withPayment} open={open} />
      ))}
    </div>
  );
}

export function BookingsListView({
  state,
  actions,
  top,
  hideEmptyUpcoming = false,
}: BookingsListRuntime & { readonly top?: ReactNode; readonly hideEmptyUpcoming?: boolean }) {
  // Placed on a custom page (web-studio-sites D4): no frame.
  const placed = usePlaced();
  const ready = state.status === 'ready';
  // The top place stays mounted, in the same spot, while the page reads: its pieces read the same upcoming bookings, and
  // one mounting after a failed read would read it again, which loads the page, unmounts them, fails, and so on.
  if (!ready && !top) return null;
  return (
    <div className={placed ? undefined : `${shared.wrap} ${css.page}`} {...pieceRoot(contract)} {...part('page')}>
      {ready && (
        <div className={css.pgHead} {...part('head')}>
          <div {...part('title')}>
            <h1 className={css.pgTitle}>Bookings</h1>
            <p className={css.pgSub}>Your upcoming sessions &amp; history</p>
          </div>
          <a className={`${shared.btn} ${shared.btnOutline} ${account.btnSm}`} href={state.bookHref} onClick={onLink(() => actions.open(state.bookHref))} {...part('book')}>
            Book a session
          </a>
        </div>
      )}
      {top}
      {ready && <Lists state={state} actions={actions} hideEmptyUpcoming={hideEmptyUpcoming} />}
    </div>
  );
}

/** Upcoming and Past, once the page has read. `hideEmptyUpcoming`: the next session, shown above, was the only one. */
function Lists({ state, actions, hideEmptyUpcoming }: Pick<BookingsListRuntime, 'state' | 'actions'> & { readonly hideEmptyUpcoming: boolean }) {
  const n = state.upcoming.length;
  const showUpcoming = !(hideEmptyUpcoming && n === 0 && state.upcomingStatus !== 'error');
  return (
    <>
      {showUpcoming && (
        <div className={css.secHead}>
          <h2 className={css.h2}>Upcoming</h2>
          {n > 0 && <span>{n === 1 ? '1 session' : `${n} sessions`}</span>}
        </div>
      )}
      {!showUpcoming ? null : state.upcomingStatus === 'error' ? (
        <div className={css.empty} role="alert" {...part('error')}>
          <p>Couldn’t load your upcoming sessions.</p>
          <button type="button" className={`${shared.btn} ${shared.btnOutline} ${account.btnSm}`} onClick={actions.retryUpcoming} {...part('retry')}>
            Try again
          </button>
        </div>
      ) : n > 0 ? (
        <Table label="Upcoming bookings" rows={state.upcoming} past={false} open={actions.open} />
      ) : (
        <p className={css.empty} {...part('empty')}>
          No sessions yet. When you book a session it will show up here.
        </p>
      )}

      <div className={css.secHead}>
        <h2 className={css.h2}>Past</h2>
        <span>Attended and cancelled sessions</span>
      </div>
      {state.pastStatus === 'error' ? (
        <p className={css.empty}>Couldn’t load your history.</p>
      ) : state.pastStatus === 'loading' ? null : state.past.length > 0 ? (
        <>
          <Table label="Past bookings" rows={state.past} past open={actions.open} />
          {state.hasMorePast && (
            <div className={css.more}>
              <button type="button" onClick={actions.loadMorePast} disabled={state.loadingMorePast} {...part('more', state.loadingMorePast && 'disabled')}>
                {state.loadingMorePast ? 'Loading…' : 'Show earlier sessions'}
                <Icon name="down" small />
              </button>
            </div>
          )}
        </>
      ) : (
        <p className={css.empty} {...part('empty')}>
          Nothing here yet. Your attended and cancelled sessions will appear here.
        </p>
      )}
    </>
  );
}
